import { EntityConstants, ICustomTypedEntity, IEntityMinimalModel, IGenericEntity } from "../../../api/GenericTypes";
import {
  Control,
  FieldErrors,
  Path,
  UnpackNestedValue,
  useController,
  UseFormGetValues,
  UseFormReset,
  UseFormSetValue,
  useWatch,
} from "react-hook-form";
import {
  processCustomTypeYupSchema,
  processInitialValuesForTypedEntity,
  processValuesForTypedEntity,
} from "./CustomTypeValidationUtils";
import * as yup from "yup";
import { useCustomTypes, UseCustomTypesProps } from "./useCustomTypes";
import { useState, useMemo, useEffect, useCallback } from "react";
import { AlertModal } from "../../../common/modals/AlertModal/AlertModal";
import { MultiEditMappedItemObject } from "../../../common/forms/MultiEditForms/common/MultiEditProvider";
import { CustomType, customTypeConstants, CustomTypeFilters } from "../../../api/CustomTypes/CustomTypes";
import { CustomTypeFormRenderer } from "./CustomTypeFormRenderer";
import { CustomTypesVirtualizedSelectForm } from "../../../common/forms/EntityForms/formsVirtualized/CustomTypesVirtualizedSelectForm";
import { InventoryItemsVirtualizedSelectForm } from "../../../common/forms/EntityForms/formsVirtualized/InventoriesVirtualizedSelectForm";
import { yupResolver } from "@hookform/resolvers/yup";
import { useGetMutation } from "../../../api/BaseEntityApi";
import { CustomTypeEntityAddFormProps } from "../../../common/entity/EntityInterfaces";

/**
 * A custom hook that provides a resolver function for handling form validation
 * with dynamic schemas based on a custom type. This is particularly useful
 * when the validation schema needs to adapt based on the `customType` property
 * of the provided entity.
 * @author @CorradoSurmanowicz
 * @template Entity - A generic type that extends `ICustomTypedEntity`, representing
 * the entity being validated.
 *
 * @param formSchema - A Yup object shape that defines the base validation schema
 * for the form.
 *
 * @returns An object containing:
 * - `typedResolver`: A callback function that dynamically resolves the validation
 *   schema based on the `customType` property of the entity. If a `customType` is
 *   present, it fetches the corresponding custom type data and processes it into
 *   a Yup schema. Otherwise, it uses the provided `formSchema`.
 *
 * @example
 * ```tsx
 * const formSchema = {
 *   name: yup.string().required(),
 *   age: yup.number().min(18),
 * };
 *
 * const { typedResolver } = useCustomTypeResolver<MyEntityType>(formSchema);
 *
 * const resolver = typedResolver(data, context, options);
 * ```
 */
export const useCustomTypeResolver = <Entity extends Partial<ICustomTypedEntity>>(formSchema: yup.ObjectShape) => {
  const { mutateAsync } = useGetMutation<CustomType, CustomTypeFilters>(customTypeConstants.resource);
  const typedResolver = useCallback(
    async (data: Entity, context: any, options: any) => {
      let yupSchema: yup.AnySchema;
      if (data.customType && data.customType.id > 0) {
        const customType = await mutateAsync({ id: data.customType.id });
        yupSchema = yup.object().shape(processCustomTypeYupSchema(customType, formSchema));
      } else {
        yupSchema = yup.object().shape(formSchema);
      }
      return yupResolver(yupSchema)(data as UnpackNestedValue<Entity>, context, options);
    },

    [formSchema, mutateAsync]
  );
  return { typedResolver };
};

interface UseCustomTypeFormProps<Entity extends ICustomTypedEntity>
  extends UseCustomTypesProps, CustomTypeEntityAddFormProps {
  control: Control<Partial<Entity>, any, Partial<Entity>>;
  reset: UseFormReset<Partial<Entity>>;
  getValues: UseFormGetValues<Partial<Entity>>;
  featureFlag?: boolean;
  typesOverride?: MultiEditMappedItemObject<CustomType>;
}

export const useCustomTypeForm = <Entity extends ICustomTypedEntity>({
  control,
  reset,
  getValues,
  filters,
  entityType,
  featureFlag = true,
  typeId,
  typesOverride,
}: UseCustomTypeFormProps<Entity>) => {
  const { types } = useCustomTypes({ entityType, filters, enabled: featureFlag });

  const _type = useWatch({
    name: "customType" as Path<Partial<Entity>>,
    control: control,
  }) as IEntityMinimalModel<number> | null;

  const customType = useMemo(() => (_type?.id ? types?.[_type.id] : undefined), [types, _type]);

  const {
    field: { onChange },
  } = useController({
    name: "customType" as Path<Partial<Entity>>,
    control: control,
  });

  useEffect(() => {
    if (!!typeId && types && types.hasOwnProperty(typeId)) {
      const customType = types[typeId];
      onChange(customType);
    }
  }, [onChange, typeId, types]);

  useEffect(() => {
    if (featureFlag) {
      const resetValues = processInitialValuesForTypedEntity(getValues(), customType);
      reset(resetValues);
    }
  }, [customType, featureFlag, getValues, reset]);

  const processCustomFields = useCallback(
    (entity: Partial<Entity>) => {
      if (!featureFlag) return entity;
      return processValuesForTypedEntity(entity, customType);
    },
    [featureFlag, customType]
  );

  return { processCustomFields, types: typesOverride ?? types };
};

interface EntityCustomTypeFormProps<Entity extends IGenericEntity & ICustomTypedEntity> {
  entityType: CustomType["entityType"];
  types: MultiEditMappedItemObject<CustomType> | undefined;
  control: Control<Partial<Entity>, any, Partial<Entity>>;
  setValue: UseFormSetValue<Partial<Entity>>;
  initialValues: Partial<Entity> | undefined;
  errors: FieldErrors<Partial<Entity>>;
  required?: boolean;
  disabled?: boolean;
  entityConstants: EntityConstants;
  allowUnassigned?: boolean;
  disableTypeSelection?: boolean;
  disableParentSelection?: boolean;
  disableTypeChangeWarning?: boolean;
  customTypeFilters?: CustomTypeFilters;
}
export const EntityCustomTypeForm = <Entity extends IGenericEntity & ICustomTypedEntity>({
  entityType,
  types,
  control,
  setValue,
  initialValues,
  errors,
  required = false,
  disabled = false,
  entityConstants,
  allowUnassigned = true,
  disableTypeSelection = false,
  disableParentSelection = false,
  disableTypeChangeWarning = false,
  customTypeFilters,
}: EntityCustomTypeFormProps<Entity>) => {
  const [showAlertModal, setShowAlertModal] = useState(false);

  const _type = useWatch({
    name: "customType" as Path<Partial<Entity>>,
    control: control,
  }) as IEntityMinimalModel<number> | null;

  const customType = useMemo(() => (_type?.id !== undefined ? types?.[_type.id] : undefined), [types, _type]);

  const {
    field: { value, onChange },
    fieldState: { isDirty },
  } = useController({
    name: "customType" as Path<Partial<Entity>>,
    control: control,
  });

  const [typeWasEmpty] = useState(value === undefined);

  useEffect(() => {
    // A check to warn users when switching types
    if (!disableTypeChangeWarning && !typeWasEmpty && isDirty) {
      setShowAlertModal(true);
    }
    if ((value as IEntityMinimalModel)?.id === -2) {
      onChange(undefined);
    }
  }, [disableTypeChangeWarning, isDirty, onChange, typeWasEmpty, value]);

  return (
    <>
      {types && !!Object.keys(types).length && (
        <CustomTypesVirtualizedSelectForm
          id={"customType" as Path<Partial<Entity>>}
          control={control}
          horizontal
          showControls
          allowUnassigned={allowUnassigned}
          filters={{ ...customTypeFilters, entityTypes: [entityType] }}
          required={required}
          disabled={disabled || disableTypeSelection}
        />
      )}
      {customType ? (
        <>
          {customType.rootHierarchy?.id && (
            <InventoryItemsVirtualizedSelectForm
              id={"parent" as Path<Partial<Entity>>}
              control={control}
              horizontal
              // required={!!type.parentMandatory}
              required
              filters={{
                sortBy: [{ property: "NAME", sortDirection: "ASC" }],
                customTypeIds: customType.parentTypes?.map((t) => t.id),
              }}
              label="Parent inventory item"
              disabled={disabled || disableParentSelection}
              customTypeConstraint={customType.parentTypes}
            />
          )}

          <CustomTypeFormRenderer<Entity>
            customType={customType}
            control={control as any}
            errors={errors}
            disabled={disabled}
          />
        </>
      ) : null}
      <AlertModal
        type="warning"
        showModal={showAlertModal}
        setShowModal={setShowAlertModal}
        title={`Confirm ${entityConstants.entitySingular} type change`}
        description={`Changing the ${entityConstants.entitySingular} type may result in loss of specific data associated with the current type. If you are unsure, please consult your system administrator before proceeding.`}
        proceedLabel="Yes, change type"
        cancelLabel="No, keep current type"
        onProceed={() => setShowAlertModal(false)}
        onCancel={() => {
          setValue("customType" as Path<Partial<Entity>>, (initialValues?.customType ?? null) as any);
          setShowAlertModal(false);
        }}
      />
    </>
  );
};
